import React from "react";
import { Input } from "@/components/atoms";
import { useCmsFormSaving } from "@/components/organisms/CmsForm";
import type { CmsFieldBaseProps } from "./CmsFieldBase";
import { CmsFieldShell } from "./CmsFieldShell";
import "./CmsField.scss";

interface CmsFieldProps extends CmsFieldBaseProps {
  label: string;
  id: string;
  multiline?: boolean;
}

type CmsFieldPropsWithInput = CmsFieldProps &
  (
    | ({
        multiline?: false;
        numberSteppers?: boolean;
      } & React.InputHTMLAttributes<HTMLInputElement>)
    | ({ multiline: true } & React.TextareaHTMLAttributes<HTMLTextAreaElement>)
  );

/** CMS form field: label + input/textarea with validation support. */
export function CmsField({
  label,
  id,
  hint,
  multiline,
  error,
  requiredMark,
  className,
  colSpan,
  disabled,
  ...rest
}: CmsFieldPropsWithInput) {
  const isFormSaving = useCmsFormSaving();
  const isDisabled = disabled || isFormSaving;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <CmsFieldShell
      label={label}
      id={id}
      hint={hint}
      error={error}
      requiredMark={requiredMark}
      className={className}
      colSpan={colSpan}
    >
      {multiline ? (
        <Input
          id={id}
          multiline
          hasError={Boolean(error)}
          className="cms-field__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          disabled={isDisabled}
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <Input
          id={id}
          hasError={Boolean(error)}
          className="cms-field__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          disabled={isDisabled}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
        />
      )}
    </CmsFieldShell>
  );
}
